import { Card, CardContent, CardHeader } from "@/components/layouts/Card";
import { useUser } from "@/contexts/UserContext";
import { OptionList } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface OptionListSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedOptionLists: OptionList[]) => void;
  initialSelectedOptionLists: OptionList[];
}

const OptionListSelectionModal: React.FC<OptionListSelectionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialSelectedOptionLists,
}) => {
  const { t } = useTranslation();
  const [axiosInstance, loading] = useAxiosInstance();
  const { user } = useUser();
  const [searchResults, setSearchResults] = useState<OptionList[]>([]);
  const [allOptions, setAllOptions] = useState<OptionList[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOptionLists, setSelectedOptionLists] = useState<OptionList[]>(
    [],
  );
  const [isFetching, setIsFetching] = useState(false);

  // Initialize the selected options when modal is opened
  useEffect(() => {
    if (isOpen) {
      setSearchResults([]);
      setSearchQuery("");
      setSelectedOptionLists(
        [...initialSelectedOptionLists].sort(
          (a, b) => (a.ranking || 0) - (b.ranking || 0),
        ),
      );
      fetchOptionList();
    }
  }, [isOpen, initialSelectedOptionLists]);

  const fetchOptionList = async () => {
    if (loading || !axiosInstance || !user?.selectedBranch) return;
    setIsFetching(true);

    try {
      const apiUrl =
        import.meta.env.VITE_APP_REDBIRDPOSBE_OPTIONLIST_INFORMATION ?? "";
      let url = `${apiUrl}?branch=${user.selectedBranch.id}`;
      const options = [];
      while (url) {
        const response = await axiosInstance.get(url);
        const { results, next } = response.data;
        options.push(...results);
        url = next;
      }
      setAllOptions(options);
      setSearchResults(options);
    } catch (error) {
      console.error("Error searching option lists:", error);
      toast.error("Failed to search option list");
    } finally {
      setIsFetching(false);
    }
  };

  // Debounced search function
  const debouncedSearch = debounce(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsFetching(true);
    const filteredOptions = allOptions.filter((option) =>
      option.name.toLowerCase().includes(query.toLowerCase()),
    );
    setSearchResults(filteredOptions);
    setIsFetching(false);
  }, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleAddOption = (option: OptionList) => {
    setSelectedOptionLists((prev) => {
      if (prev.some((o) => o.id === option.id)) return prev;

      const newRanking =
        prev.length > 0 ? Math.max(...prev.map((o) => o.ranking || 0)) + 1 : 1;

      return [...prev, { ...option, ranking: newRanking }];
    });
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleRemoveOption = (optionId: number) => {
    setSelectedOptionLists((prev) => prev.filter((o) => o.id !== optionId));
  };

  const handleSave = () => {
    const uniqueRankedOptionLists = [...selectedOptionLists].map(
      (item, index) => ({
        ...item,
        ranking: index + 1,
      }),
    );

    onSave(uniqueRankedOptionLists);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <Card className="flex h-[80vh] w-full max-w-lg flex-col">
        <CardHeader className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("Select Option Lists")}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            &times;
          </button>
        </CardHeader>
        <div className="px-6 py-4">
          <div className="relative mb-4">
            <div className="flex">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t("Search Option...")}
                className="flex-grow rounded-l-md border border-gray-300 px-3 py-2"
              />
              <button className="rounded-r-md bg-gray-500 px-3 py-2 text-white">
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </div>

            {searchQuery && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                {isFetching ? (
                  <div className="p-2 text-center text-gray-500">
                    {t("Searching...")}
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((option) => (
                    <div
                      key={option.id}
                      className="flex cursor-pointer items-center justify-between p-2 hover:bg-gray-100"
                      onClick={() => handleAddOption(option)}
                    >
                      <div className="space-x-2">
                        <span>{option.name}</span>
                        {option.required && (
                          <span className="ml-2 rounded bg-gray-200 px-2 py-1 text-sm">
                            {t("Required")}
                          </span>
                        )}
                      </div>
                      <FontAwesomeIcon
                        icon={faPlus}
                        className="ml-2 text-gray-500"
                      />
                    </div>
                  ))
                ) : searchQuery.trim() && !isFetching ? (
                  <div className="p-2 text-center text-gray-500">
                    {t("No results found")}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <CardContent className="h-full flex-grow overflow-y-auto">
          <div className="h-full space-y-2">
            <h3 className="mb-2 font-medium">Selected Option Lists</h3>

            {/* Selected options */}
            <div className="flex flex-col space-y-2">
              {selectedOptionLists.length > 0 &&
                selectedOptionLists.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-700">
                        {option.name}
                      </span>
                      {option.required && (
                        <span className="rounded bg-gray-200 px-2 py-1 text-sm">
                          {t("Required")}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveOption(option.id)}
                      className="text-red-500 hover:bg-red-50 hover:text-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}

              <div className="my-4 border-t border-gray-200" />
            </div>

            {/* Unselected options */}
            <h3 className="mb-2 font-medium">Available Option Lists</h3>
            <div className="max-h-full space-y-2 overflow-y-auto">
              {allOptions
                .filter((option) =>
                  selectedOptionLists.every((o) => o.id !== option.id),
                )
                .map((option) => {
                  return (
                    <div
                      key={option.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          onChange={() => handleAddOption(option)}
                          className="h-4 w-4"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {option.name}
                        </span>
                        {option.required && (
                          <span className="rounded bg-gray-200 px-2 py-1 text-sm">
                            {t("Required")}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </CardContent>
        <div className="flex justify-end border-t p-4">
          <button
            onClick={onClose}
            className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleSave}
            className="rounded-md bg-red-500 px-4 py-2 text-white"
          >
            {t("Confirm")}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default OptionListSelectionModal;
